<template>
  <div class="block" >
    <h2 class="page-head"><span class="page-heading">Certificate Program</span></h2>
    <p>The Safe Food Handler is an&nbsp;ANSI-ANAB&nbsp;accredited program (<a class="external-link" href="https://www.ansi.org/accreditation/credentialing/certificate-issuers/AllDirectoryDetails?&amp;prgID=212,238&amp;OrgId=2249&amp;statusID=4" rel="nofollow">Accreditation ID 1243</a>), and is published by Cogneti LLC. The program is self-guided, allows unlimited exam attempts, and is designed for easy, anytime use as a mobile app (web browser, iPhone, or Android).</p>
    <p>FOOD HANDLER cards (badges) and certificates issued by this certificate program are accepted by:</p>
    <ol>
      <li>California</li>
      <li>Illinois</li>
      <li>Arizona</li>
      <li>West Virginia</li>
      <li>Texas</li>
      <li>New Mexico</li>
      <li>Hawaii</li>
      <li>Other local jurisdictions that require food handlers to have a certificate from an ANSI-ANAB accredited organization.</li>
    </ol>

    <div class="block-header">
      <h2><span class="title">Purpose</span></h2>
    </div>
    <p>The purpose of this Safe Food Handler certificate program is to instruct, assess, and issue the FOOD HANDLER certificate to qualified program participants.</p>

    <div class="block-header">
      <h2><span class="title">Scope</span></h2>
    </div>
    <p>The program scope includes the FOOD HANDLER knowledge and skills of the current FDA Food Code, released Supplements to the Food Code, and generally accepted industry practices.</p>


    <div class="block-header">
      <h2><span class="title">Learning Outcomes</span></h2>
    </div>
    <p>At the end of the program you will be able to demonstrate FOOD HANDLER knowledge and skill in these topical areas.</p>
    <ol>
      <li>Foodborne illness</li>
      <li>Time/temperature control for safety</li>
      <li>Employee hygiene</li>
      <li>Receiving food products</li>
      <li>Storing food products</li>
      <li>Preparing and serving food</li>
      <li>Cleaning and sanitizing</li>
    </ol>


    <div class="block-header">
      <h2><span class="title">Participation Requirements</span></h2>
    </div>
      <p>Here is what you need to use the Safe Food Handler Certificate Program.</p>
      <ul>
        <li>Access to a web browser, an iPhone, iPad, or Android phone or tablet.</li>
        <li>A valid email address so you can register with the program and authenticate your name and email address.</li>
        <li>Payment of the full program’s $15 license fee (Levels 1 & 2 are a free trial). Payment is made within the certificate program app, after you register and sign in.</li>
        <li>A minimum 'seat time' of approximately two hours to LEARN, QUIZ, and complete the EXAM. There is no time limit on how quickly you finish the program. Some students may take longer than others. Learn at your own pace and convenience.</li>
        <li>The program is only available in English language.</li>
      </ul>
      <p>You access the certificate program from three kinds of devices:</p>
      <ol>
        <li>From a web browser application go to <a href="http://safefoodhandler.cogneti.com" target="_blank">safefoodhandler.cogneti.com</a></li>
        <li>From an iPhone or iPad download <strong>Safe Food Handler</strong> from the <a href="https://apps.apple.com/us/app/safe-food-handler/id1114444303" target="_blank">App Store</a></li>
        <li>From any Android phone or tablet download the <strong>Food Handler Certificate</strong> from the <a
          href="https://play.google.com/store/apps/details?id=com.cogneti.android.foodhandler" target="_blank">Google Play Store</a></li>
      </ol>
      <p>Each Safe Food Handler certificate program application delivers the same Certificate Program, with the same licensing, sign-in credentials, and ANSI-ANAB-accredited certificate. Moving between devices? No problem, the program will pick up where you left off as you continue your learning.

      </p>
      <p>The applications guide you quickly thru seven levels of instruction, quizzes, and examination (assessment). The apps also manage registration, licensing, and issuing of the certificate and eBadge.

      </p>
    <p><strong>NOTE:</strong>  <em>Safe Food Handler</em> is the name of the app on iTunes and the App Store for Apple iOS users. For Android users, the program app is called <strong>Food Handler Certificate</strong> when downloading from the Play Store.

      </p>

    <div class="block-header">
      <h2><span class="title">Program Steps to Completion</span></h2>
    </div>

    <p>All steps must be completed. There is no provision for partial work or previous experience.</p>
    <ol>
      <li>Access the Safe Food Handler Certificate Program
        <ol style="list-style: lower-alpha;">
          <li>From a web browser application go to&nbsp;<a class="external-link" href="http://safefoodhandler.cogneti.com/" rel="nofollow">safefoodhandler.cogneti.com</a></li>
          <li>From any iPhone or iPad download <strong>Safe Food Handler</strong> from the <a href="https://apps.apple.com/us/app/safe-food-handler/id1114444303" target="_blank">App Store</a></li>
          <li>From any Android phone or tablet download <strong>Food Handler Certificate</strong> from the <a href="https://play.google.com/store/apps/details?id=com.cogneti.android.foodhandler" target="_blank">Google Play Store</a></li>
        </ol>
      </li>
      <li>Use the&nbsp;<strong>LEARN&nbsp;</strong>function to view the seven levels of course instruction. For each level read the material and practice your recall by taking a quiz. Do this several times to increase your recall and skill. Experienced food handles may simply need a quick review.</li>
      <li>Complete a ten-question&nbsp;<strong>QUIZ</strong>&nbsp;for each of the seven levels of instruction.Practice your recall by taking a quiz several times to increase your recall and skill. When you finish reading and quizzing at one level the next level automatically opens for you.</li>
      <li>Once you have read and quizzed at all seven levels the <strong>EXAM</strong> is available for you to attempt. Pass the exam with at least a <strong>70% score</strong>. If you do not pass an exam, the app will show where you need to study by marking that level in red. You must complete additional quizzes on that level before the exam will unlock for another attempt. We’ll also send you an email detailing your exam performance.

      </li>
      <li>When you <strong>PASS</strong> the exam, you will be emailed a program certificate that has a unique ID number and is valid for 3-years, along with an eBadge. The eBadge works as a handy and portable form of showing your accomplishment and program expertise. The eBadge unique number lets you or employers validate your certificate at
        <a href="https://cogneti.com/badge" target="_blank">cogneti.com/badge</a>.

      </li>
    </ol>

    <div class="block-header">
      <h2><span class="title">Training Accelerators</span></h2>
    </div>

    <p>The certificate program has special features called training accelerators that will speed up the learning process.</p>
    <p><u>Accelerator 1:</u>&nbsp;During quizzes, use&nbsp;<strong>Coins</strong>&nbsp;and&nbsp;<strong>Peeks</strong>.&nbsp;Immediately locate the right information to answer any quiz question &ndash; a great way to quickly learn!</p>

    <ul>
      <li>Earn Coins by reading subject views.</li>
      <li>Redeem Coins for Peeks during quizzes.</li>
      <li>A Peek button shows just the right information needed to answer a quiz question.</li>
    </ul>

    <p class="mt-2"><u>Accelerator 2</u>: A&nbsp;<strong>Skill Level</strong>&nbsp;shows the expected learning outcome for each course level. The skill level is a result of your subject coverage and measured recall when you complete a quiz.</p>
    <ul>
      <li>Skill Level is re-calculated each time you take a quiz.</li>
      <li>Use the quiz a few times and get closer to 100%.</li>
      <li>Your goal is a Skill Level of Expert.</li>
    </ul>

      <p class="mt-2"><u>Accelerator 3</u>:&nbsp;<strong>Quiz Time</strong>&nbsp;measures the time you took to complete a ten-question quiz.</p>
    <ul>
      <li>Aim for a fast Quiz Time<em>&nbsp;</em>(two minutes or less!). This can be done by practice and repeat use of the quiz.</li>
      <li>Fast quiz times show confidence and easy subject recall.</li>
    </ul>

    <p class="mt-2"><u>Accelerator 4</u>:&nbsp;<strong>Page View Tracking</strong>&nbsp;will check off the course&nbsp;pages that you&rsquo;ve viewed. This helps you track what you&rsquo;ve read and what you haven&rsquo;t.<

    <div class="block-header">
      <h2><span class="title">Post Certificate Activities</span></h2>
    </div>

    <ol>
      <li><strong>Issued Certificate</strong>: When you pass the exam, your course certificate is issued. We send it to the email address you registered when you began the program. It identifies you by name and shows an expiration date three years from the date you passed the exam. You receive a unique badge ID, registered to your email and user name. The ID allows you to validate your certificate at&nbsp;<a class="external-link" href="http://cogneti.com/badge" rel="nofollow">cogneti.com/badge</a></li>
      <li><strong>eBadge:&nbsp;</strong>Once you receive your ebadge, you can email it to employers, print a paper copy, or order a laminated copy with your photograph (about the size of a credit card). The eBadge serves as portable evidence of the Safe Food Handler certificate. The eBadge is sent to your email address. Save it.</li>
      <li><strong>Re-Issue Course Certificate</strong>The Safe Food Handler certificate expires after three years. You may obtain a re-issued course certificate, by completing all steps of the program again. You will have to complete the quizzes on all seven-course levels and, yes, you’ll also need to take and pass the exam.</li>
      <li><strong>Practice and Skills Maintenance:&nbsp;</strong>The Safe Food Handler app is a tool to practice and maintain your skills, any time and any place. If you forget the holding time for food at a catered event, you’ve got the answer in your pocket on your phone. The app is a reference for you to use and review.</li>
    </ol>


    <div class="block-header">
      <h2><span class="title">Refunds</span></h2>
    </div>
    <p>All license-fee sales are final. Refunds may be requested by issuing a support request using the Safe Food Handler mobile app. Consideration of the refund request will be made solely at the discretion of Cogneti staff.

    </p>



    <div class="block-header">
      <h2><span class="title">General Program Notifications</span></h2>
    </div>

    <ul>
      <li>
      Cogneti, LLC does not receive any commercial or third-party support in the creation of the SFH certificate program. The awarding of a certificate is based solely on the individual performance of the student.
      </li>
      <li>
      The program content is updated to the current FDA Food Code and includes all applicable supplements.
      </li>
      <li>
      Per Cogneti Privacy Policy, personal identification information (names, email, addresses, telephone numbers), communications, and training records provided by program participants are held confidential. Certain information (name, email, phone number, and program status) is made available to an employer when approved by the program participant. Personal identification information will not be provided or sold to advertisers or third-parties (except by lawful court order).
      </li>
      <li>
      No academic or continuing education credit is earned by completion of this certificate program.
      </li>
</ul>
    <div class="font-weight-bold text-xs-right mt-5 mb-3">
      (Reference CAP 5.2.1 - 5.2.1.3 & 5.5 – 5.5.2.1 & 5.6 - 5.6.3 & 6.1.3 - 6.1.3.4 & 6.1.4 - 6.1.4.2)
    </div>
  </div>
</template>

<script>
export default {
  name: 'CertificateProgramContent'
}
</script>



// WEBPACK FOOTER //
// src/components/views/guest/CertificateProgram/Program62.vue